// PowerComposer — the "Power" direction promoted to a production component,
// pure/props-driven.
//
// Promotes sketches/chat-composer/PowerComposerSketch.tsx (the owner's
// picked direction, .claude/analysis/chat-composer-research.md) into the real
// composer surface: markdown-as-you-type, `/` slash-commands, `@`-mentions,
// the Send/Stop/Retry state machine (SendStateButton), and an in-frame
// context row for the audience + mentioned sources. SELF-CONTAINED: every
// intent (send/stop/retry, a mention picked, a command picked, a draft
// edit) reports UP via a typed callback — there is no wiring to the turn
// pipeline, the transcript, or any port here. That wiring is a later
// integration pass; this component's whole public contract is its props.
//
// === Markdown-as-you-type: the mirror-overlay technique =====================
// (the syntax-highlighted-textarea overlay, the react-simple-code-editor /
// CodeMirror5 lineage): a styled `<pre>` mirror and the real `<textarea>`
// share one CSS grid cell with identical box + typography. The textarea's
// own text is painted TRANSPARENT (only its native caret and selection
// show); the mirror carries the visible, styled glyphs. `markdownSegments.ts`
// does the (pure) block + inline parsing; `**bold**`/`*italic*`/`` `code` ``
// /list markers/fenced code all render live, their raw syntax markers
// staying visible-but-dimmed rather than vanishing out from under the
// cursor — an author should never see characters disappear as they type.
//
// KNOWN LIMITATION, stated rather than hidden (chat-composer-research.md §3
// names this cost explicitly, and the sketch this promotes flagged it
// first): the mirror and the invisible textarea stay pixel-aligned only
// while a segment shares the plain-text glyph metrics. **Bold**
// (font-weight 600 vs 400) and monospace `` `code` `` / fenced code-block
// LINES both shift character widths in this app's proportional typeface, so
// the visible caret can drift a few px from the styled glyph beneath it
// once a line mixes those spans with plain text. Italic (rendered via
// colour + dotted underline, never font-style) and list markers (colour
// only) carry NO width-shifting channel, so they stay exactly aligned. This
// is the direction's real, structural build cost — a full fix needs either
// a rich-text editor (ProseMirror/Lexical-class dependency) or a
// monospace-locked font across every style — not something to chase away
// with more CSS. Shipped as-is because the drift is narrow (bold + code
// only), never destructive (no character is ever lost — Enter/Backspace/
// selection all operate on the real, plain-text textarea underneath), and
// the alternative (no live markdown at all) was explicitly rejected by the
// owner's direction pick.
//
// === The two comboboxes ======================================================
// `/` (whole draft starts with it) opens the slash-command menu; `@`
// (caret-anchored, anywhere in the draft) opens the mention menu — distinct
// trigger characters, distinct row content, per research doc §3 ("slash
// commands invoke actions... @-mentions reference context... distinct
// trigger characters and menu styling"). Both lean on the same
// `aria-activedescendant` combobox mechanics already established in this
// codebase (CommandPalette.tsx, Editor/SlashMenu, TopStrip's omnibar): the
// TEXTAREA keeps DOM focus and points at the active row via
// `aria-activedescendant` + `aria-controls` (no `aria-expanded` — see the
// inline comment on the textarea below); the popup
// (ComposerListbox) is `role="listbox"`. One difference from those
// `<input>`-based combobox bars, axe-verified: `role="combobox"` is only an
// ALLOWED explicit role on single-line elements (`<input type="text">`) per
// the ARIA-in-HTML mapping — axe's `aria-allowed-role` rejects it on a
// `<textarea>`. This textarea therefore keeps its native implicit
// "textbox" role (which itself supports `aria-activedescendant` since ARIA
// 1.2, precisely for this "rich compose box with autocomplete" case) rather
// than adding an explicit role the element does not support. A picked
// mention is written INLINE at its position as `[title](/library/{id})` (D8,
// the agent's own link form), its title highlighted in the mirror; the
// context row's chips are the position-less references (attached files,
// dropped Library rows).
//
// === Never lock the surface ==================================================
// `status` names the TURN pipeline's state (idle/streaming/error);
// `disabled` names the composer's OWN inert state. Streaming never disables
// the textarea or the menus — only the send identity swaps to Stop
// (SendStateButton) — matching the research doc's named anti-pattern
// (§2, §7.1 point 4): "never lock the surface" / Doherty-threshold
// discipline extended past a single response.

import { Trans, useLingui } from "@lingui/react/macro";
import type { DragEvent, ReactNode } from "react";
import { useId, useLayoutEffect, useRef, useState } from "react";
import { Icon } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import { VisuallyHidden } from "../../VisuallyHidden/VisuallyHidden.js";
import { ComposerListbox } from "./ComposerListbox.js";
import {
    detectMentionTrigger,
    detectSlashTrigger,
    insertMention,
    isDismissed,
} from "./composerTriggers.js";
import { decideComposerArrow } from "./historyNavGating.js";
import {
    type MarkdownLine,
    type MarkdownSegment,
    parseMarkdownLines,
} from "./markdownSegments.js";
import { ResourceMentionChip } from "./ResourceMentionChip.js";
import { mentionsAfterDrop, RESOURCE_DRAG_MIME } from "./resourceDrag.js";
import { SendStateButton } from "./SendStateButton.js";
import { isSendChord, type SendKey } from "./sendKey.js";
import { deriveSendState, type PowerComposerStatus } from "./sendState.js";
import type {
    ComposerAudience,
    ComposerResourceKind,
    ComposerResourceRef,
    ComposerSlashCommand,
} from "./types.js";
import "./PowerComposer.css";

export type { SendKey } from "./sendKey.js";
export type {} from "./sendState.js";
export type {
    ComposerAudience,
    ComposerResourceKind,
    ComposerResourceRef,
    ComposerSlashCommand,
} from "./types.js";

// Which way the `/`-slash and `@`-mention menus open. A named union so the
// direction is a bounded prop, not a loose boolean. `below` is the default
// (there is room beneath the field); `above` is for a BOTTOM-anchored composer
// (the chat's foot-of-transcript seat) where opening down would collide with
// the viewport edge and shove the composer up — opening up flips the menu into
// the free space above instead. Deterministic per mount context (the host
// knows its own anchoring); it does not measure the viewport, so it never
// auto-re-flips mid-open (see the CSS note).
export type ComposerMenuPlacement = "below" | "above";

// Who OWNS the context display. `inline` (default) — PowerComposer renders its
// OWN context row (the audience chip + the mentioned-source chips), the shipped
// behaviour every current caller relies on. `external` — an outside surface (a
// ContextCard placed above the composer, e.g. the workspace landing) owns the
// context display, so the composer SUPPRESSES its own row to avoid
// double-display while KEEPING `@`-mention picking fully working: mentions
// still drive the menu's already-picked dedup and are still reported up via
// `onMentionsChange` (the external card reads and edits that same list). A
// named union, not a boolean flag, so the intent reads at the call site.
export type PowerComposerContextDisplay = "inline" | "external";

// Where the composer sits in a conversation's life. `new` — the new-chat
// landing, before anything was sent; `running` — under a conversation. The AI
// disclaimer belongs only to a running conversation (owner, 2026-10-06): on
// the landing there is no AI output yet to verify. A named union, not a
// boolean, so the call site reads as the fact it states.
type ComposerConversationPhase = "new" | "running";

export type PowerComposerProps = {
    // The draft text — controlled, same discipline as Composer.tsx: the
    // caller owns it, every edit (typed, or a slash/mention pick) reports
    // up via `onValueChange`.
    readonly value: string;
    readonly onValueChange: (value: string) => void;

    // The resources the author has mentioned into this draft so far —
    // controlled, so the integration layer can read the exact set to
    // attach when the turn is sent. Rendered as removable chips in the
    // context row.
    readonly mentions: readonly ComposerResourceRef[];
    readonly onMentionsChange: (
        mentions: readonly ComposerResourceRef[],
    ) => void;

    // The turn pipeline's own state — idle/streaming/error. Drives
    // SendStateButton's identity via `deriveSendState`; never disables the
    // textarea or the menus (see header: "never lock the surface").
    readonly status: PowerComposerStatus;
    // Fires when the author commits a non-empty draft (Send click, or the
    // send-key chord). The caller decides whether/when to clear `value` —
    // PowerComposer never clears its own controlled props.
    readonly onSend: () => void;
    // Fires when Retry is clicked (only rendered while `status.type ===
    // "error"`).
    readonly onRetry: () => void;
    // A Limit hint above the field (the safety-first pattern): a ceiling is
    // reached, so Send is off and the text stays. Absent: none.
    readonly limitHint?: string;

    // The mentionable resource pool the `@` menu offers — data-driven, no
    // hardcoded corpus. Empty by default (an integration that never passes
    // candidates gets a working but always-empty mention menu, never a
    // crash).
    readonly candidates?: readonly ComposerResourceRef[];
    // The quick actions the `/` menu offers — data-driven, no hardcoded
    // corpus.
    readonly commands?: readonly ComposerSlashCommand[];

    // The conversation-level grounding shown in the context row (today's
    // SituationProfileChip, lifted into the composer's own frame). Absent
    // ⇒ no audience chip; if `onRequestAudience` is given, a ghost
    // "Set audience" affordance takes its place (this component never owns
    // the audience PICKER itself — it only reports the intent up, same
    // contract as Composer.tsx's onAttach/onReference).
    readonly audience?: ComposerAudience;
    readonly onClearAudience?: () => void;
    readonly onRequestAudience?: () => void;

    // Enter sends by default (Jakob's law — sendKey.ts). "modEnter" is the
    // opt-in power-user remap: Cmd/Ctrl+Enter sends, plain Enter always
    // inserts a newline. Never invent a third binding.
    readonly sendKey?: SendKey;

    // The whole surface's own inert state — distinct from `status`. See
    // header.
    readonly disabled?: boolean;
    // Which way the slash/mention menus open — `below` (default) or `above`
    // for a bottom-anchored composer. See `ComposerMenuPlacement`.
    readonly menuPlacement?: ComposerMenuPlacement;
    // Who owns the context display — `inline` (default, the composer's own row)
    // or `external` (an outside ContextCard). See `PowerComposerContextDisplay`.
    readonly contextDisplay?: PowerComposerContextDisplay;
    // `running` (default) shows the AI disclaimer above the box; `new` (the
    // landing) omits it. See `ComposerConversationPhase`.
    readonly conversationPhase?: ComposerConversationPhase;

    // An optional leading affordance rendered before the text field in the
    // composer's own row (the "+" attach menu, passed by both the landing and
    // the conversation). Absent ⇒ nothing renders. The composer stays props-
    // driven: whatever the slot contains reports its own intents up through
    // its own callbacks, never through PowerComposer.
    readonly leadingSlot?: ReactNode;

    // Message-history navigation (A-11), shell/readline style. Arrow-Up recalls
    // an OLDER sent message, Arrow-Down returns toward the live draft — but ONLY
    // when the caret gating below is satisfied, so in-body vertical caret
    // movement is untouched. The composer stays props-driven: it fires the
    // intent up and the host's ChatSession FSM decides the concrete recall (the
    // composer holds no history cursor of its own — it is not remounted per
    // conversation, so local state would leak across tabs).
    //   - `onRecallOlder` — Arrow-Up, no selection, caret on the FIRST logical
    //     line. Absent ⇒ Arrow-Up is plain caret movement.
    //   - `onRecallNewer` — Arrow-Down, no selection, caret on the LAST logical
    //     line, AND `historyNavActive`. Absent ⇒ plain caret movement.
    //   - `historyNavActive` — true while the host is mid-recall (`at_history`).
    //     Arrow-Down only ever recalls when already recalling (you cannot "go
    //     newer" without first going older), matching shell/address-bar feel.
    readonly onRecallOlder?: () => void;
    readonly onRecallNewer?: () => void;
    readonly historyNavActive?: boolean;
    readonly className?: string;
};

// A closed, non-nesting **bold**/`code`/*italic* span becomes one styled
// span; its opening/closing markers stay in the DOM (dimmed) rather than
// disappearing, so nothing an author typed vanishes silently.
const renderSegment = (segment: MarkdownSegment, key: number): ReactNode => {
    if (segment.type === "plain") {
        return <span key={key}>{segment.text}</span>;
    }
    if (segment.type === "link") {
        return (
            <span key={key} className="bilda-composer-power__md-link">
                <span className="bilda-composer-power__md-marker">[</span>
                {segment.title}
                <span className="bilda-composer-power__md-marker">
                    {segment.text.slice(segment.title.length + 1)}
                </span>
            </span>
        );
    }
    const markerLength = segment.type === "bold" ? 2 : 1;
    const inner = segment.text.slice(markerLength, -markerLength);
    const open = segment.text.slice(0, markerLength);
    const close = segment.text.slice(-markerLength);
    return (
        <span key={key} className={`bilda-composer-power__md-${segment.type}`}>
            <span className="bilda-composer-power__md-marker">{open}</span>
            {inner}
            <span className="bilda-composer-power__md-marker">{close}</span>
        </span>
    );
};

const renderLine = (line: MarkdownLine, key: number): ReactNode => {
    switch (line.type) {
        case "code-fence":
            return (
                <span key={key} className="bilda-composer-power__md-marker">
                    {line.text}
                </span>
            );
        case "code-body":
            return (
                <span key={key} className="bilda-composer-power__md-code-line">
                    {line.text}
                </span>
            );
        case "list-item":
            return (
                <span key={key}>
                    <span className="bilda-composer-power__md-marker">
                        {line.marker}
                    </span>
                    {line.segments.map(renderSegment)}
                </span>
            );
        case "text":
            return <span key={key}>{line.segments.map(renderSegment)}</span>;
    }
};

export const PowerComposer = ({
    value,
    onValueChange,
    mentions,
    onMentionsChange,
    status,
    onSend,
    onRetry,
    limitHint,
    candidates = [],
    commands = [],
    audience,
    onClearAudience,
    onRequestAudience,
    sendKey = "enter",
    disabled = false,
    menuPlacement = "below",
    contextDisplay = "inline",
    conversationPhase = "running",
    leadingSlot,
    onRecallOlder,
    onRecallNewer,
    historyNavActive = false,
    className,
}: PowerComposerProps) => {
    const { t } = useLingui();
    const reactId = useId();
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // The author-facing singular kind noun for a mention candidate row.
    // Closes over `t` (the lingui macro is only valid as a tagged template
    // at a literal call site, never invoked as a plain function) — mirrors
    // CommandPalette.tsx's `headingFor`/`nounFor`.
    const kindLabel = (kind: ComposerResourceKind): string => {
        switch (kind) {
            case "source":
                return t`Source`;
            case "material":
                return t`Material`;
            case "package":
                return t({ message: "Package", context: "resource kind" });
            case "file":
                return t`File`;
        }
    };

    const [caret, setCaret] = useState(value.length);
    const [activeSlashIndex, setActiveSlashIndex] = useState(0);
    const [activeMentionIndex, setActiveMentionIndex] = useState(0);
    const [dismissedSlashQuery, setDismissedSlashQuery] = useState<
        string | null
    >(null);
    const [dismissedMentionQuery, setDismissedMentionQuery] = useState<
        string | null
    >(null);
    // A pick (slash/mention) rewrites `value` from OUTSIDE the textarea's
    // own onChange, so the browser does not know where the caret should
    // land. This queues the target position; the layout effect below moves
    // the real DOM selection once the new value has painted.
    const [pendingCaret, setPendingCaret] = useState<number | null>(null);

    useLayoutEffect(() => {
        if (pendingCaret === null) return;
        const el = textareaRef.current;
        if (el !== null) {
            el.focus();
            el.setSelectionRange(pendingCaret, pendingCaret);
        }
        setCaret(pendingCaret);
        setPendingCaret(null);
        // Deliberately keyed on `pendingCaret` alone: it is the ONE queued
        // intent this effect drains, and `textareaRef`/`setCaret`/
        // `setPendingCaret` are all referentially stable.
    }, [pendingCaret]);

    const markdownLines = parseMarkdownLines(value);
    const hasDraft = value.trim() !== "";
    // At a ceiling (the Limit hint shows) Send is off; the text stays.
    const sendState = deriveSendState(
        status,
        hasDraft && limitHint === undefined,
    );

    const slashTrigger = detectSlashTrigger(value);
    const slashOpen =
        !disabled &&
        slashTrigger !== null &&
        !isDismissed(slashTrigger.query, dismissedSlashQuery);
    const slashMatches =
        slashTrigger === null
            ? []
            : commands.filter((c) =>
                  c.label
                      .toLowerCase()
                      .includes(slashTrigger.query.toLowerCase()),
              );
    const clampedSlashIndex = Math.min(
        activeSlashIndex,
        Math.max(slashMatches.length - 1, 0),
    );

    const mentionTrigger = detectMentionTrigger(value, caret);
    const mentionOpen =
        !disabled &&
        // Slash-mode is whole-composer (the entire draft is the command
        // query, research doc §3's "invoke" reading), so it takes priority
        // over an @ run that happens to also match — the two menus never
        // render at once.
        !slashOpen &&
        mentionTrigger !== null &&
        !isDismissed(mentionTrigger.query, dismissedMentionQuery);
    const mentionMatches =
        mentionTrigger === null
            ? []
            : candidates.filter(
                  (c) =>
                      !mentions.some((m) => m.id === c.id) &&
                      c.title
                          .toLowerCase()
                          .includes(mentionTrigger.query.toLowerCase()),
              );
    const clampedMentionIndex = Math.min(
        activeMentionIndex,
        Math.max(mentionMatches.length - 1, 0),
    );

    const slashListboxId = `${reactId}-slash-listbox`;
    const slashOptionId = (i: number) => `${reactId}-slash-opt-${i}`;
    const mentionListboxId = `${reactId}-mention-listbox`;
    const mentionOptionId = (i: number) => `${reactId}-mention-opt-${i}`;

    const commitSlash = (command: ComposerSlashCommand) => {
        onValueChange(command.insertText);
        setPendingCaret(command.insertText.length);
        setDismissedSlashQuery(null);
        setActiveSlashIndex(0);
    };

    const commitMention = (candidate: ComposerResourceRef) => {
        if (mentionTrigger === null) return;
        const next = insertMention(value, mentionTrigger, caret, candidate);
        onValueChange(next.value);
        setPendingCaret(next.caret);
        setDismissedMentionQuery(null);
        setActiveMentionIndex(0);
    };

    const trySend = () => {
        if (disabled || sendState.type !== "ready") return;
        onSend();
    };

    // A Library row dropped on the composer becomes an `@`-mention (once),
    // reported up through the same `onMentionsChange` an `@`-pick uses. Only
    // our resource MIME is claimed — OS file drags fall through to IngestHost's
    // window listener (upload → source → composer chip).
    const carriesResource = (event: DragEvent<HTMLElement>) =>
        !disabled && event.dataTransfer.types.includes(RESOURCE_DRAG_MIME);
    const onResourceDragOver = (event: DragEvent<HTMLElement>) => {
        if (!carriesResource(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
    };
    const onResourceDrop = (event: DragEvent<HTMLElement>) => {
        if (!carriesResource(event)) return;
        event.preventDefault();
        const next = mentionsAfterDrop(
            mentions,
            event.dataTransfer.getData(RESOURCE_DRAG_MIME),
        );
        if (next !== null) onMentionsChange(next);
    };

    const readCaret = (el: HTMLTextAreaElement) =>
        setCaret(el.selectionStart ?? el.value.length);

    return (
        // F74 — the shell owns the width so the disclaimer can sit ABOVE the
        // composer box (a sibling), not inside it. The box keeps the caller's
        // className (autofocus/tour target) + the elevated surface.
        <div className="bilda-composer-power-shell">
            {/* The disclaimer sits ABOVE the composer box (owner, 2026-10-06). */}
            {conversationPhase === "running" ? (
                <p className="bilda-composer-power__disclaimer">
                    <Trans>
                        AI can make mistakes, always verify content before you
                        use it
                    </Trans>
                </p>
            ) : null}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: the composer frame is a DROP TARGET for Library rows (native HTML5 DnD has no interactive-role equivalent). The `@`-menu is the primary, keyboard-operable way to mention a resource; drag is additive sugar. */}
            <div
                className={`bilda-composer-power${className ? ` ${className}` : ""}`}
                onDragOver={onResourceDragOver}
                onDrop={onResourceDrop}
                data-disabled={disabled || undefined}
                aria-disabled={disabled || undefined}
                data-menu-placement={menuPlacement}
            >
                {limitHint === undefined ? null : (
                    // The Limit hint (CL-21): the quiet note pattern.
                    <div className="bilda-composer-power__note" role="status">
                        <Icon name="info" size="xs" />
                        <Text as="span" size="xs">
                            {limitHint}
                        </Text>
                    </div>
                )}
                {status.type === "error" ? (
                    <div className="bilda-composer-power__error" role="alert">
                        <Icon name="danger" size="xs" />
                        <Text as="span" size="xs">
                            {status.message}
                        </Text>
                    </div>
                ) : status.type === "created" ? (
                    // G3-interim-B1: a calm, NON-alert note (role="status") — the
                    // interrupted turn's course survived, so we reassure + point to
                    // review instead of arming a blind Retry that would double-create.
                    <div className="bilda-composer-power__note" role="status">
                        <Icon name="material" size="xs" />
                        <Text as="span" size="xs">
                            <Trans>
                                Your course was created — review it, then
                                continue.
                            </Trans>
                        </Text>
                    </div>
                ) : null}

                {/* The field takes the full width; the bar below it
                carries the attach control, the grounding chips and send
                (the redesign's composer). */}
                <div className="bilda-composer-power__field">
                    {/* The visible, styled mirror — decorative only; the
                    real accessible value + placeholder live on the
                    textarea beneath it. */}
                    <pre
                        className="bilda-composer-power__mirror"
                        aria-hidden="true"
                    >
                        {markdownLines.flatMap((line, i) =>
                            i === 0
                                ? [renderLine(line, i)]
                                : ["\n", renderLine(line, i)],
                        )}
                        {value === "" || value.endsWith("\n") ? " " : null}
                    </pre>
                    <textarea
                        ref={textareaRef}
                        className="bilda-composer-power__input"
                        rows={1}
                        value={value}
                        disabled={disabled}
                        // Short enough for the narrow chat panel: an opening
                        // ask on a new conversation, a reply once it runs.
                        placeholder={
                            conversationPhase === "running"
                                ? t`Reply to the assistant…`
                                : t`Ask the assistant anything…`
                        }
                        aria-label={t`Message`}
                        // NOT aria-expanded: axe's aria-allowed-attr rejects
                        // it on a textarea's native "textbox" role (unlike
                        // the input-based combobox bars elsewhere in this
                        // codebase). `aria-controls`' presence already
                        // signals "a popup exists"; `aria-activedescendant`
                        // says which row is active — together they carry
                        // the same information a sighted user gets from the
                        // menu simply being visible.
                        aria-controls={
                            slashOpen
                                ? slashListboxId
                                : mentionOpen
                                  ? mentionListboxId
                                  : undefined
                        }
                        aria-activedescendant={
                            slashOpen && slashMatches.length > 0
                                ? slashOptionId(clampedSlashIndex)
                                : mentionOpen && mentionMatches.length > 0
                                  ? mentionOptionId(clampedMentionIndex)
                                  : undefined
                        }
                        aria-autocomplete="list"
                        onChange={(event) => {
                            onValueChange(event.target.value);
                            readCaret(event.target);
                        }}
                        onKeyUp={(event) => readCaret(event.currentTarget)}
                        onClick={(event) => readCaret(event.currentTarget)}
                        onKeyDown={(event) => {
                            if (slashOpen) {
                                if (event.key === "ArrowDown") {
                                    event.preventDefault();
                                    setActiveSlashIndex((i) =>
                                        Math.min(
                                            i + 1,
                                            Math.max(
                                                slashMatches.length - 1,
                                                0,
                                            ),
                                        ),
                                    );
                                    return;
                                }
                                if (event.key === "ArrowUp") {
                                    event.preventDefault();
                                    setActiveSlashIndex((i) =>
                                        Math.max(i - 1, 0),
                                    );
                                    return;
                                }
                                if (
                                    event.key === "Enter" &&
                                    !event.nativeEvent.isComposing
                                ) {
                                    event.preventDefault();
                                    const pick =
                                        slashMatches[clampedSlashIndex];
                                    if (pick !== undefined) commitSlash(pick);
                                    return;
                                }
                                if (event.key === "Escape") {
                                    event.preventDefault();
                                    setDismissedSlashQuery(
                                        slashTrigger?.query ?? null,
                                    );
                                    return;
                                }
                            }
                            if (mentionOpen) {
                                if (event.key === "ArrowDown") {
                                    event.preventDefault();
                                    setActiveMentionIndex((i) =>
                                        Math.min(
                                            i + 1,
                                            Math.max(
                                                mentionMatches.length - 1,
                                                0,
                                            ),
                                        ),
                                    );
                                    return;
                                }
                                if (event.key === "ArrowUp") {
                                    event.preventDefault();
                                    setActiveMentionIndex((i) =>
                                        Math.max(i - 1, 0),
                                    );
                                    return;
                                }
                                if (
                                    event.key === "Enter" &&
                                    !event.nativeEvent.isComposing
                                ) {
                                    event.preventDefault();
                                    const pick =
                                        mentionMatches[clampedMentionIndex];
                                    if (pick !== undefined) commitMention(pick);
                                    return;
                                }
                                if (event.key === "Escape") {
                                    event.preventDefault();
                                    setDismissedMentionQuery(
                                        mentionTrigger?.query ?? null,
                                    );
                                    return;
                                }
                            }
                            // Message-history recall (A-11), placed AFTER both
                            // menu blocks: an open slash/mention menu already
                            // preventDefault+returned on Arrow-Up/Down above, so
                            // control only reaches here when neither is open —
                            // menu precedence for free (ordering). Caret/selection
                            // are read LIVE off the DOM (event.currentTarget),
                            // same discipline as `readCaret`, never the lagging
                            // `caret` state; the pure `decideComposerArrow` does
                            // the gating (also re-checks menuOpen defensively).
                            // A `none` verdict falls through to native textarea
                            // caret movement — in-body vertical nav untouched.
                            const recall = decideComposerArrow({
                                key: event.key,
                                menuOpen: slashOpen || mentionOpen,
                                selectionStart:
                                    event.currentTarget.selectionStart,
                                selectionEnd: event.currentTarget.selectionEnd,
                                value,
                                historyNavActive,
                                canRecallOlder: onRecallOlder !== undefined,
                                canRecallNewer: onRecallNewer !== undefined,
                            });
                            if (recall.type === "recall_older") {
                                event.preventDefault();
                                onRecallOlder?.();
                                return;
                            }
                            if (recall.type === "recall_newer") {
                                event.preventDefault();
                                onRecallNewer?.();
                                return;
                            }
                            if (
                                isSendChord(
                                    {
                                        key: event.key,
                                        shiftKey: event.shiftKey,
                                        metaKey: event.metaKey,
                                        ctrlKey: event.ctrlKey,
                                        composing:
                                            event.nativeEvent.isComposing,
                                    },
                                    sendKey,
                                )
                            ) {
                                event.preventDefault();
                                trySend();
                            }
                        }}
                    />
                </div>
                <div className="bilda-composer-power__bar">
                    {leadingSlot != null ? (
                        <div className="bilda-composer-power__leading">
                            {leadingSlot}
                        </div>
                    ) : null}
                    {/* Context row — the conversation's grounding, lifted into the
                    composer's own frame (research doc §4/§7.2: "scope is shown
                    before it's spent"). Renders only when there is something to
                    show or offer, so a bare, unGrounded turn keeps the resting
                    composer minimal (progressive disclosure). */}
                    {contextDisplay === "inline" &&
                    (audience !== undefined ||
                        onRequestAudience !== undefined ||
                        mentions.length > 0) ? (
                        <div className="bilda-composer-power__context">
                            {audience !== undefined ? (
                                <span className="bilda-composer-power__audience">
                                    <Icon name="person" size="xs" />
                                    <Text as="span" size="xs" weight="medium">
                                        {audience.title}
                                    </Text>
                                    {onClearAudience !== undefined &&
                                    !disabled ? (
                                        <button
                                            type="button"
                                            className="bilda-composer-power__chip-remove"
                                            onClick={onClearAudience}
                                        >
                                            <VisuallyHidden>
                                                <Trans>Clear audience</Trans>
                                            </VisuallyHidden>
                                            <Icon name="close" size="xs" />
                                        </button>
                                    ) : null}
                                </span>
                            ) : onRequestAudience !== undefined ? (
                                <button
                                    type="button"
                                    className="bilda-composer-power__ghost"
                                    disabled={disabled}
                                    onClick={onRequestAudience}
                                >
                                    <Icon name="plus" size="xs" />
                                    <Text as="span" size="xs">
                                        <Trans>Set audience</Trans>
                                    </Text>
                                </button>
                            ) : null}

                            {mentions.map((m) => (
                                <ResourceMentionChip
                                    key={m.id}
                                    resourceRef={m}
                                    description={kindLabel(m.kind)}
                                    onRemove={
                                        disabled
                                            ? undefined
                                            : () =>
                                                  onMentionsChange(
                                                      mentions.filter(
                                                          (x) => x.id !== m.id,
                                                      ),
                                                  )
                                    }
                                />
                            ))}
                        </div>
                    ) : null}

                    <span className="bilda-composer-power__spacer" />
                    <SendStateButton
                        state={disabled ? { type: "idle" } : sendState}
                        onSend={trySend}
                        onRetry={onRetry}
                    />
                </div>

                {slashOpen ? (
                    <ComposerListbox
                        listboxId={slashListboxId}
                        ariaLabel={t`Quick actions`}
                        items={slashMatches}
                        activeIndex={clampedSlashIndex}
                        emptyLabel={
                            <Text as="span" size="sm" tone="dim">
                                <Trans>No matching actions</Trans>
                            </Text>
                        }
                        optionId={slashOptionId}
                        keyOf={(item) => item.id}
                        onCommit={commitSlash}
                        onHoverIndex={setActiveSlashIndex}
                        renderOption={(item) => (
                            <span className="bilda-composer-power__option-body">
                                <Text as="span" size="sm" weight="medium">
                                    {item.label}
                                </Text>
                                <Text as="span" size="xs" tone="dim">
                                    {item.description}
                                </Text>
                            </span>
                        )}
                    />
                ) : null}

                {mentionOpen ? (
                    <ComposerListbox
                        listboxId={mentionListboxId}
                        ariaLabel={t`Mention a resource`}
                        items={mentionMatches}
                        activeIndex={clampedMentionIndex}
                        emptyLabel={
                            <Text as="span" size="sm" tone="dim">
                                <Trans>No matching resources</Trans>
                            </Text>
                        }
                        optionId={mentionOptionId}
                        keyOf={(item) => item.id}
                        onCommit={commitMention}
                        onHoverIndex={setActiveMentionIndex}
                        renderOption={(item) => (
                            <>
                                <Text as="span" size="xs" tone="dim">
                                    {kindLabel(item.kind)}
                                </Text>
                                <Text as="span" size="sm">
                                    {item.title}
                                </Text>
                            </>
                        )}
                    />
                ) : null}
            </div>
            {/* F74 — the quiet AI marker sits UNDER the composer box (owner), a
                sibling below it, dim + small so it orients without disturbing
                the flow. Only once a conversation is running (see
                `ComposerConversationPhase`). */}
        </div>
    );
};
